import { DecisionActionResult, DecisionDetail, DecisionPage } from "../../models/home";
import { HomeDashboardStore } from "./home.component.dashboard-store";

/** Applies server action outcomes to the cached dashboard slices. */
export class HomeDecisionStatePatcher {
  constructor(private readonly store: HomeDashboardStore) {}

  apply(decisionId: string, result: DecisionActionResult): void {
    this.patchOverviewState(decisionId, result);
    this.patchDecisionListState(decisionId, result);
    this.patchCalendarDecisionState(decisionId, result);
    this.patchNextBestDecisionState(decisionId, result);
    this.patchDecisionDetailState(decisionId, result);
  }
  patchOverviewState(id: string, result: DecisionActionResult): void { this.patchPageState(this.store.latestOverviewState, this.store.overviewPatch$, id, result); }
  patchDecisionListState(id: string, result: DecisionActionResult): void { this.patchPageState(this.store.latestDecisionListState, this.store.decisionListPatch$, id, result); }
  patchCalendarDecisionState(id: string, result: DecisionActionResult): void { this.patchPageState(this.store.latestCalendarDecisionState, this.store.calendarDecisionPatch$, id, result); }

  patchNextBestDecisionState(decisionId: string, result: DecisionActionResult): void {
    const current = this.store.latestNextBestDecisionState;
    const next = current?.data;
    if (!current || !next) return;
    const shouldPatchDecision = next.decision?.decisionId === decisionId;
    if (!shouldPatchDecision && !result.updatedSummary) return;
    const decision = shouldPatchDecision && next.decision ? this.patchListItem(next.decision, result) : next.decision;
    this.store.nextBestDecisionPatch$.next({
      ...current,
      data: {
        ...next, decision, nextAction: result.updatedDecision?.nextBestAction ?? next.nextAction,
        summary: result.updatedSummary ?? next.summary
      },
      loading: false, error: null, empty: !decision, stale: false, lastUpdated: Date.now()
    });
  }

  patchDecisionDetailState(decisionId: string, result: DecisionActionResult): void {
    const current = this.store.latestDecisionDetailState;
    if (!current?.data?.decision || current.data.decision.decisionId !== decisionId) return;
    const decision = this.patchDetailItem(current.data.decision, result);
    this.store.decisionDetailPatch$.next({
      ...current, data: { ...current.data, decision }, loading: false, error: null, empty: !decision,
      stale: false, lastUpdated: Date.now()
    });
  }

  patchDecisionPage(page: DecisionPage, decisionId: string, result: DecisionActionResult): DecisionPage | null {
    if (!page.items.some((item) => item.decisionId === decisionId) && !result.updatedSummary) return null;
    return {
      ...page,
      items: page.items.map((item) => item.decisionId === decisionId ? this.patchListItem(item, result) : item),
      summary: result.updatedSummary ?? page.summary
    };
  }

  patchListItem(item: DecisionPage["items"][number], result: DecisionActionResult): DecisionPage["items"][number] {
    const updated = result.updatedDecision;
    return {
      ...item,
      state: updated?.state ?? result.newState ?? item.state, title: updated?.title ?? item.title,
      shortReason: updated?.shortReason ?? item.shortReason,
      documentCount: updated?.documents?.length ?? item.documentCount,
      primaryDocument: updated?.documents?.[0] ?? item.primaryDocument,
      nextBestAction: updated?.nextBestAction ?? item.nextBestAction,
      availableActions: result.availableActions ?? updated?.availableActions ?? item.availableActions,
      priority: updated?.priority ?? item.priority, risk: updated?.risk ?? item.risk,
      confidence: updated?.confidence ?? item.confidence,
      missingInformationCount: updated?.missingInformation?.length ?? item.missingInformationCount,
      owner: updated?.owner ?? item.owner, dueAt: updated?.dueAt ?? item.dueAt,
      updatedAt: updated?.updatedAt ?? item.updatedAt, traceId: updated?.traceId ?? item.traceId
    };
  }

  patchDetailItem(
    decision: NonNullable<DecisionDetail["decision"]>,
    result: DecisionActionResult
  ): NonNullable<DecisionDetail["decision"]> {
    const updated = result.updatedDecision;
    return {
      ...decision, ...updated, state: updated?.state ?? result.newState ?? decision.state,
      nextBestAction: updated?.nextBestAction ?? decision.nextBestAction,
      availableActions: result.availableActions ?? updated?.availableActions ?? decision.availableActions,
      documents: updated?.documents ?? decision.documents, evidence: updated?.evidence ?? decision.evidence,
      missingInformation: updated?.missingInformation ?? decision.missingInformation,
      owner: updated?.owner ?? decision.owner, priority: updated?.priority ?? decision.priority,
      risk: updated?.risk ?? decision.risk, confidence: updated?.confidence ?? decision.confidence,
      dueAt: updated?.dueAt ?? decision.dueAt, updatedAt: updated?.updatedAt ?? decision.updatedAt,
      traceId: updated?.traceId ?? decision.traceId, sourceTrace: updated?.sourceTrace ?? decision.sourceTrace
    };
  }

  private patchPageState(
    current: typeof this.store.latestOverviewState,
    patch$: typeof this.store.overviewPatch$,
    decisionId: string,
    result: DecisionActionResult
  ): void {
    if (!current?.data) return;
    const page = this.patchDecisionPage(current.data, decisionId, result);
    if (page) patch$.next({
      ...current, data: page, loading: false, error: null, empty: page.items.length === 0,
      stale: false, lastUpdated: Date.now()
    });
  }
}
